const DEFAULT_PLAYER_BG = ["#e3f2fd", "#c8e6c9", "#fff9c4", "#ffe0b2"];
const DEFAULT_PLAYER_BORDER = ["#64b5f6", "#81c784", "#fff176", "#ffb74d"];

export function isValidHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const match = /^#([0-9A-Fa-f]{6})$/.exec(hex);
  if (!match) return null;
  return {
    r: parseInt(match[1].slice(0, 2), 16),
    g: parseInt(match[1].slice(2, 4), 16),
    b: parseInt(match[1].slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((c) =>
      Math.min(255, Math.max(0, Math.round(c)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")}`;
}

export function darkenHex(hex: string, factor = 0.72): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(rgb.r * factor, rgb.g * factor, rgb.b * factor);
}

function srgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function getRelativeLuminance(r: number, g: number, b: number): number {
  return (
    0.2126 * srgbToLinear(r) +
    0.7152 * srgbToLinear(g) +
    0.0722 * srgbToLinear(b)
  );
}

/** Choisit noir ou blanc selon le meilleur ratio de contraste WCAG sur le fond. */
export function getContrastTextColor(backgroundHex: string): string {
  const rgb = hexToRgb(backgroundHex);
  if (!rgb) return "#000000";

  const bgLuminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
  const contrastWithWhite = (1.05) / (bgLuminance + 0.05);
  const contrastWithBlack = (bgLuminance + 0.05) / 0.05;

  return contrastWithWhite >= contrastWithBlack ? "#ffffff" : "#000000";
}

export function normalizeBoardLiveColor(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return isValidHexColor(trimmed) ? trimmed : undefined;
}

export function getPlayerCardColors(
  boardLiveColor: string | undefined,
  playerIndex: number
): { backgroundColor: string; borderColor: string; textColor: string } {
  if (boardLiveColor && isValidHexColor(boardLiveColor)) {
    return {
      backgroundColor: boardLiveColor,
      borderColor: darkenHex(boardLiveColor),
      textColor: getContrastTextColor(boardLiveColor),
    };
  }
  const i = playerIndex % DEFAULT_PLAYER_BG.length;
  const backgroundColor = DEFAULT_PLAYER_BG[i];
  return {
    backgroundColor,
    borderColor: DEFAULT_PLAYER_BORDER[i],
    textColor: getContrastTextColor(backgroundColor),
  };
}
